/**
 * 生年月日から満年齢を求めるユーティリティ（管理画面のクルー登録用）。
 *
 * ワーカー側フロント（nity-line-front/src/utils/age.ts）とバックエンド
 * （app/api/admin/workers.py の _calc_age / MIN_AGE）と同じ判定に揃えている。
 * リポジトリが別なので実装を共有できず、ここに同じものを置いている。
 *
 * 月だけを比較する実装（age === 16 && monthDiff < 0 のような判定）は、誕生月かつ
 * 誕生日前（例: 生年月日 8/20 / 今日 8/5）を取りこぼして実年齢が1歳下の人を通してしまう。
 * ここでは日まで比較する。
 *
 * また `new Date('YYYY-MM-DD')` は UTC 深夜として解釈されるため、getMonth()/getDate() を
 * ローカル時刻で読むと環境によって1日ずれる。文字列を分解して数値比較する。
 */

/** 登録に必要な最低年齢 */
export const MIN_AGE = 16;

/** 16歳未満のときに画面へ出す文言（バックエンドの UNDER_AGE_MESSAGE と揃える） */
export const UNDER_AGE_MESSAGE = `${MIN_AGE}歳未満のクルーは登録できません`;

/**
 * 満年齢を返す。'YYYY-MM-DD' 形式以外・存在しない日付（2026-02-30 等）は null を返す。
 */
export const calcAge = (birthDate: string, today: Date = new Date()): number | null => {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthDate.trim());
  if (!matched) return null;

  const year = Number(matched[1]);
  const month = Number(matched[2]);
  const day = Number(matched[3]);

  // 存在しない日付を弾く（Date に通すと 2026-02-30 → 3/2 のように繰り上がる）
  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }

  let age = today.getFullYear() - year;
  const monthDiff = today.getMonth() + 1 - month;
  // 誕生日がまだ来ていない年は1歳引く（同月なら日で判定）
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) {
    age -= 1;
  }
  return age;
};

/** 満 MIN_AGE 歳以上かどうか。未入力・不正な日付は false（送信させない） */
export const isOldEnough = (birthDate: string, today: Date = new Date()): boolean => {
  const age = calcAge(birthDate, today);
  return age !== null && age >= MIN_AGE;
};
